import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { ENUM__page_type } from '@/enums/ENUM__page_type';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_navigate_ud } from '@/hooks/_base/use_navigate_ud';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { ButtonComponentActionEmpty } from '@/pages/_base/buttons/ButtonComponentActionEmpty';
import { ButtonWorkspaceChildEntityDelete } from '@/pages/_base/buttons/ButtonWorkspaceChildEntityDelete';
import { LOCAL__generation_set_current } from '@/state/generation/LOCAL__generation_set_current';
import { string_to_span } from '@/utils/utils_html';
import { assert_array_not_empty } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTO__database_entity } from '@shared/dto/_database_entity/DTO__database_entity';
import { DTO__user_task } from '@shared/dto/user_task/DTO__user_task';
import { format_timestamp_YY_MM_DD_HH_mm_ss } from '@shared/utils/utils_time';
import { useMemo, useState } from 'react';
import { ButtonWorkspaceChildEntityShare } from '../../../_base/buttons/ButtonWorkspaceChildEntityShare';
import { U_BUTTON__user_task_change_name } from './_buttons/U_BUTTON__user_task_change_name';
import { U_BUTTON__user_task_change_visibility } from './_buttons/U_BUTTON__user_task_change_visibility';
import { U_BUTTON__user_task_duplicate } from './_buttons/U_BUTTON__user_task_duplicate';
import { U_LABEL__user_task_name } from './U_LABEL__user_task_name';

interface Props {
  user_task: DTO__user_task;
  set_is_edit_name: (value: boolean) => void;
}

export function U_LIST_ITEM__user_task({ user_task, set_is_edit_name }: Props) {
  const dispatch = use_dispatch();
  const navigate = use_navigate_ud();

  const { generation_current, user_task_current } = use_slice_generation(['generation_current', 'user_task_current']);
  const { global_is_desktop, global_is_mobile } = use_slice_ui(['global_is_desktop', 'global_is_mobile']);

  /*
   *
   */
  const [tooltip_title, set_tooltip_title] = useState(user_task.name);

  const component_id = `${ENUM__component_type.PAGE_USER_TASKS_LIST_ITEM}_${user_task._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_USER_TASK);

  const [is_mouse_over, set_is_mouse_over] = useState(false);

  const [is_button_delete_clicked, setIsButtonDeleteClicked] = useState(false);

  const is_selected = () => generation_current.user_task_id === user_task._id;

  const is_any_generation_in_progress = useMemo(() => user_task.generations.some(generation => generation.is_in_progress), [user_task.generations]);

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (is_button_delete_clicked) return;

    set_is_mouse_over(false);
  }, [is_button_delete_clicked]);

  use_effect(() => {
    set_tooltip_title(user_task.name.is_blank() ? format_timestamp_YY_MM_DD_HH_mm_ss(user_task.created_at) : user_task.name);
  }, [user_task]);

  /*
   * EVENTS
   */

  function onMouseLeaveItem() {
    if (is_button_delete_clicked) return;

    set_is_mouse_over(false);
  }

  function onClick() {
    if (user_task_current._id == user_task._id) return; //already selected

    let generations = [...user_task.generations];
    generations.sort(DTO__database_entity.sort_by_updated_at_newest_first);

    assert_array_not_empty(generations);
    let most_recent_generation = generations[0];
    dispatch(LOCAL__generation_set_current(most_recent_generation._id));
  }

  async function onDoubleClick() {
    onClick(); //select user task

    await navigate(ENUM__page_type.GENERATION_WITHOUT_PARAMS);
  }

  /*
   * METHODS
   */

  /*
   * RENDERS
   */

  function render_button_share() {
    let out = null;

    if (is_mouse_over || user_task.is_shared || global_is_mobile) {
      out = (
        <ButtonWorkspaceChildEntityShare
          isSelected={is_selected() || is_mouse_over}
          item={user_task}
          required_permission={ENUM__workspace_entity_feature.ENTITY_USER_TASK_SHARE}
        />
      );
    }

    return out;
  }

  function render_button_duplicate() {
    let out = null;

    if (is_mouse_over || global_is_mobile) {
      out = <U_BUTTON__user_task_duplicate item={user_task} isSelected={is_selected()} />;
    }

    return out;
  }

  function render_button_change_visibility() {
    let out = null;

    if (is_mouse_over || global_is_mobile) {
      out = <U_BUTTON__user_task_change_visibility item={user_task} isSelected={is_selected()} />;
    }

    return out;
  }

  function render_button_delete() {
    let out = null;

    if (is_mouse_over || global_is_mobile) {
      out = (
        <ButtonWorkspaceChildEntityDelete
          entity={user_task}
          required_permission={ENUM__workspace_entity_feature.ENTITY_USER_TASK_DELETE}
          isSelected={is_selected()}
          {...{ setIsButtonDeleteClicked }}
        />
      );
    }

    return out;
  }

  function render_button_edit_name() {
    if (!is_mouse_over && global_is_desktop) return null;

    return <U_BUTTON__user_task_change_name setIsEditName={set_is_edit_name} isSelected={is_selected()} />;
  }

  function render_buttons() {
    let out = <></>;

    if (!is_mouse_over && user_task.is_shared) {
      out = (
        <>
          <ButtonComponentActionEmpty />
          {render_button_share()}
          <ButtonComponentActionEmpty />
        </>
      );
    } else {
      out = (
        <>
          {render_button_edit_name()}
          {render_button_duplicate()}
          {render_button_change_visibility()}
          {render_button_share()}
          {render_button_delete()}
        </>
      );
    }

    return <div className="flex items-center justify-start gap-1">{out}</div>;
  }

  return (
    <UI_PRIMITIVE__tooltip delay_duration={0} side="right" className="!h-auto" tooltip_title={tooltip_title.length > 35 ? string_to_span(tooltip_title) : ''}>
      <div
        {...{ onClick, onDoubleClick }}
        className={`list-item-wrapper relative hover-animation px-1  ${is_any_generation_in_progress ? 'border border-purchase' : ''}  
        ${user_task.is_hidden ? 'opacity-50' : ''}
        ${is_mouse_over || is_selected() ? 'list-item-selected' : ''}
        `}
        onMouseEnter={() => set_is_mouse_over(true)}
        onMouseLeave={() => onMouseLeaveItem()}
      >
        <NotifierBorder notifier={notifier} />
        <div className="list-item-inner">
          <U_LABEL__user_task_name item={user_task} is_any_generation_in_progress={is_any_generation_in_progress} />
          {render_buttons()}
        </div>
      </div>
    </UI_PRIMITIVE__tooltip>
  );
}
